import { AxiosError } from 'axios'
import { getDefaultStore } from 'jotai'
import { ChangeEvent, useCallback, useMemo, useState } from 'react'
import { toast } from '~components/ui/toast'
import { S3 } from '~models/s3'

import { MEDIA_FALLBACK_URL } from '~constants'
import S3Repository from '~repositories/impl/s3-repository'
import loadingAtom, { setLoading } from '~store/loading'
import { getFileKeys, getFileTypes } from '~utils/file'

export type FileUploadResponse = {
  file: File
  remoteUri: string
}

type UploadType = 'image' | 'video' | 'pdf'

type UseFileUploadProps = {
  type: UploadType
  maxNum: number
  currentFileNum: number
  resourceType: S3.ResourceType
  onChange: (response: FileUploadResponse[]) => void
  setPreviews?: (response: string[]) => void
}

const uploadImages = async (files: File[], resourceType: S3.ResourceType) => {
  try {
    setLoading(true)
    const fileTypes = await getFileTypes(files, resourceType)
    const preSignedPostUrls = await S3Repository.getPreSignedPostUrls(fileTypes)
    const photoUrls = await S3Repository.uploadMultiplePhotoToS3(files, preSignedPostUrls)

    const result = files.map((file, index) => ({
      file,
      remoteUri: photoUrls[index],
    }))

    return result
  } catch (e) {
    if (e instanceof AxiosError) {
      toast.open({
        type: 'error',
        message: e.message || 'Something went wrong. Please try again later.',
      })
    }
  } finally {
    setLoading(false)
  }
}

const uploadVideos = async (files: File[], resourceType: S3.ResourceType) => {
  try {
    setLoading(true)
    const fileTypes = await getFileTypes(files, resourceType)
    const preSignedPostUrls = await S3Repository.getPreSignedPostUrls(fileTypes)
    await S3Repository.uploadMultipleVideoToS3(files, preSignedPostUrls)
    const fileKeys = await getFileKeys(preSignedPostUrls)
    const convertedVideos = await S3Repository.getConvertedVideos(fileKeys)

    if (!convertedVideos) {
      return
    }

    const result = convertedVideos.map((item, index) => {
      return {
        file: files[index],
        remoteUri: `${item.additional_data.converted_video_url}?thumbnail_url=${item.additional_data.thumbnail_url}`,
      }
    })

    return result
  } catch (e) {
    if (e instanceof AxiosError) {
      toast.open({
        type: 'error',
        message: e.message || 'Something went wrong. Please try again later.',
      })
    }
  } finally {
    setLoading(false)
  }
}

const uploadPdfs = async (files: File[], resourceType: S3.ResourceType) => {
  try {
    setLoading(true)
    const fileTypes = await getFileTypes(files, resourceType)
    const preSignedPostUrls = await S3Repository.getPreSignedPostUrls(fileTypes)
    const uploadedPdfs = await S3Repository.uploadMultiplePdfToS3(files, preSignedPostUrls)

    const result = files.map((file, index) => ({
      file,
      remoteUri: uploadedPdfs[index],
    }))

    return result
  } catch (e) {
    if (e instanceof AxiosError) {
      toast.open({
        type: 'error',
        message: e.message || 'Something went wrong. Please try again later.',
      })
    }
  } finally {
    setLoading(false)
  }
}

const uploadFiles = async (type: UploadType, files: File[], resourceType: S3.ResourceType) => {
  if (type === 'image') {
    return uploadImages(files, resourceType)
  }

  if (type === 'video') {
    return uploadVideos(files, resourceType)
  }

  if (type === 'pdf') {
    return uploadPdfs(files, resourceType)
  }
}

const MAX_FILE_VOLUME = 1000 * 1000 * 1000 // 1GB

const openWarningToast = (message: string) => {
  const store = getDefaultStore()
  store.set(loadingAtom, false)

  toast.open({
    message,
    type: 'caution',
  })
}

const useFileUpload = ({
  maxNum,
  currentFileNum,
  type,
  resourceType,
  onChange,
  setPreviews,
}: UseFileUploadProps) => {
  const [isUploading, setIsUploading] = useState(false)
  const _onChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target

      if (!files) {
        return
      }

      setIsUploading(true)
      setLoading(true)

      const uploadingFiles = Array.from(files)

      if (currentFileNum + uploadingFiles.length > maxNum) {
        return openWarningToast(`You can upload up to ${maxNum} files.`)
      }

      for (const file of uploadingFiles) {
        if (file.size > MAX_FILE_VOLUME) {
          return openWarningToast('Each file must be under 1GB.')
        }

        /**
         * Pixel 기기에서 동영상을 바로 찍어서 업로드를 시도할 때, 사진이 찍히는 이슈가 있어서 추가됨
         */
        if (type === 'video' && file.type.split('/')[0] !== type) {
          return openWarningToast('Choose videos from your library.')
        }

        /**
         * (안드로이드) pdf/application 으로 input 포맷을 설정해도 다른 포맷 업로드가 가능한 이슈가 있어서 추가됨
         * @see https://drtail.slack.com/archives/C018C0UERB3/p1723527732379549?thread_ts=1723527017.003999&cid=C018C0UERB3
         */
        if (type === 'pdf' && file.type !== 'application/pdf') {
          return openWarningToast('Please select a PDF file.')
        }
      }

      const previewImages = uploadingFiles.map(file => {
        return {
          image: URL.createObjectURL(file),
          video: MEDIA_FALLBACK_URL,
          pdf: '',
        }[type]
      })

      setPreviews?.(previewImages)

      const response = await uploadFiles(type, uploadingFiles, resourceType)

      if (response) {
        onChange(response)
      }

      setLoading(false)
      setIsUploading(false)
      e.target.value = '' // input 초기화
    },
    [setLoading, currentFileNum, maxNum, setPreviews, type, resourceType, onChange]
  )

  const accept = useMemo(
    () =>
      ({
        image: 'image/*',
        video: 'video/*',
        pdf: 'application/pdf',
      })[type],
    [type]
  )

  const fileUploadProps = useMemo(
    () => ({
      onChange: _onChange,
      accept,
      isUploading,
    }),
    [accept, _onChange, isUploading]
  )

  return fileUploadProps
}

export default useFileUpload
